import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from "./ui/command";

/**
 * GoodwinGuard quick-jump palette (⌘K / Ctrl+K).
 * Searches every nav item the current role can access (role-filtered sections
 * from navModel) and navigates to the chosen page. Single source of truth: it
 * consumes the same `sections` the sidebar renders, so access never diverges.
 *
 * While searching we render ONE flat list (no groups): cmdk 1.1.1 can't reorder
 * groups whose headings contain emoji/spaces, which otherwise auto-selects the
 * wrong first-in-DOM item. Empty query keeps the grouped section view.
 */
export function CommandPalette({ sections = [] }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    document.addEventListener("keydown", onKey);
    const onOpenEvent = () => setOpen(true);
    window.addEventListener("open-command-palette", onOpenEvent);
    return () => {
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("open-command-palette", onOpenEvent);
    };
  }, []);

  // Reset the query whenever the dialog closes.
  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  // Flattened, path-deduped item list for the search view.
  const flatItems = useMemo(() => {
    const seen = new Set();
    const out = [];
    sections.forEach((section) => {
      section.items.forEach((item) => {
        if (seen.has(item.path)) return;
        seen.add(item.path);
        out.push({ ...item, section: section.title });
      });
    });
    return out;
  }, [sections]);

  const go = (path) => {
    setOpen(false);
    navigate(path);
  };

  const searching = query.trim().length > 0;

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput
        placeholder="Jump to a page…"
        value={query}
        onValueChange={setQuery}
        data-testid="command-palette-input"
      />
      <CommandList data-testid="command-palette-list">
        <CommandEmpty>No matching pages.</CommandEmpty>

        {searching ? (
          <CommandGroup heading="Pages">
            {flatItems.map((item) => {
              const Icon = item.icon;
              return (
                <CommandItem
                  key={item.path}
                  value={item.label}
                  keywords={[item.section, item.path]}
                  onSelect={() => go(item.path)}
                  data-testid={`command-item-${item.path}`}
                  className="cursor-pointer"
                >
                  {Icon && <Icon className="mr-2 h-4 w-4 opacity-70" />}
                  <span>{item.label}</span>
                  <span className="ml-auto text-[11px] text-muted-foreground">
                    {item.section.replace(/^[^\w]+\s*/, "")}
                  </span>
                </CommandItem>
              );
            })}
          </CommandGroup>
        ) : (
          sections.map((section, idx) => (
            <CommandGroup key={idx} heading={section.title}>
              {section.items.map((item) => {
                const Icon = item.icon;
                return (
                  <CommandItem
                    key={item.path}
                    value={item.label}
                    keywords={[section.title, item.path]}
                    onSelect={() => go(item.path)}
                    data-testid={`command-item-${item.path}`}
                    className="cursor-pointer"
                  >
                    {Icon && <Icon className="mr-2 h-4 w-4 opacity-70" />}
                    <span>{item.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          ))
        )}
      </CommandList>
    </CommandDialog>
  );
}

export default CommandPalette;
